import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { EfficiencyMetrics, EfficiencySummary, TargetsSettings, Tool } from '@ace-tracker/shared';
import { useSettings } from '../settings';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { EFFICIENCY_METRIC_INFO } from '../metricInfo';
import { TOOL_COLORS } from '../toolColors';
import { ChartCard } from './ChartCard';
import { InfoTooltip } from './InfoTooltip';
import { PricingBasisNote } from './PricingBasisNote';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

type MetricKey = 'outputPerCost' | 'reasoningRatio' | 'contextLoad' | 'cacheHitRatio';

const DASH = '—';
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

function formatMetric(key: MetricKey, value: number | null, currency: string): string {
  if (value === null) return DASH;
  if (key === 'outputPerCost') return `${compact.format(value)} per ${currency}`;
  if (key === 'contextLoad') return `${value >= 10 ? Math.round(value).toLocaleString() : value.toFixed(1)}×`;
  return `${(value * 100).toFixed(value < 0.1 ? 1 : 0)}%`;
}

const METRICS: { key: MetricKey; label: string; info: string; caption: string }[] = [
  { key: 'outputPerCost', label: 'Output per cost', info: EFFICIENCY_METRIC_INFO.outputPerCost, caption: 'Output tokens per unit of indicative cost — higher is better' },
  { key: 'cacheHitRatio', label: 'Cache hit ratio', info: EFFICIENCY_METRIC_INFO.cacheHitRatio, caption: 'Each tool’s own formula — compare each tool with itself' },
  { key: 'contextLoad', label: 'Context load', info: EFFICIENCY_METRIC_INFO.contextLoad, caption: 'Context tokens sent per output token' },
  { key: 'reasoningRatio', label: 'Reasoning ratio', info: EFFICIENCY_METRIC_INFO.reasoningRatio, caption: 'Thinking or reasoning tokens as a share of output' },
];

/** Higher-is-better goals from Settings → Budgets & goals, in the metric's own units (ratios as 0–1). */
type Goals = Partial<Record<MetricKey, Partial<Record<Tool, number>>>>;

function goalsFrom(targets: TargetsSettings | undefined): Goals {
  if (!targets) return {};
  const ratio = (percent: number | undefined) => (percent === undefined ? undefined : percent / 100);
  return {
    cacheHitRatio: { 'claude-code': ratio(targets.cacheHitGoalClaudeCode), codex: ratio(targets.cacheHitGoalCodex) },
    outputPerCost: { 'claude-code': targets.outputPerCostGoalClaudeCode, codex: targets.outputPerCostGoalCodex },
  };
}

/**
 * Efficiency view: Claude Code and Codex ratios by tool, day, model, and team.
 * Each tool is computed in its own token terms; tools are compared side by
 * side but never summed (see dashboard/AGENTS.md "API rules").
 */
export function EfficiencyDashboardBody({
  summary,
  formatCurrency,
  SectionHeading,
}: {
  summary: EfficiencySummary;
  formatCurrency: (value: number, currency: string) => string;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const { currency } = summary;
  const tools = summary.byTool.map((t) => t.tool);
  const goals = goalsFrom(useSettings().data?.targets);

  if (summary.byTool.length === 0) {
    return <p className="text-sm text-text-muted">No Claude Code or Codex usage in this range.</p>;
  }

  return (
    <>
      <section>
        <SectionHeading title="At a glance" hint="Each tool in its own token terms — compare a tool with itself over time" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {summary.byTool.map((point) => (
            <div key={point.tool} className="relative rounded-card border border-border bg-surface p-5 shadow-sm">
              <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: TOOL_COLORS[point.tool] }} />
              <h3 className="text-sm font-semibold text-text-default">{TOOL_LABELS[point.tool]}</h3>
              <p className="mt-1 text-xs text-text-muted">
                {point.requests.toLocaleString()} requests · {formatCurrency(point.cost, currency)} indicative cost
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-3 text-sm sm:grid-cols-4">
                {METRICS.map((m) => (
                  <div key={m.key}>
                    <dt className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-text-faint">
                      {m.label}
                      <InfoTooltip text={m.info} />
                    </dt>
                    <dd className="text-lg font-semibold tabular-nums text-text-default">{formatMetric(m.key, point[m.key], currency)}</dd>
                    <GoalStatus goal={goals[m.key]?.[point.tool]} value={point[m.key]} format={(v) => formatMetric(m.key, v, currency)} />
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </section>

      <section>
        <SectionHeading title="Trends" hint="Per UTC day; days without usage are gaps" />
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {METRICS.map((m) => (
            <TrendChart key={m.key} metric={m} summary={summary} tools={tools} goals={goals[m.key]} />
          ))}
        </div>
      </section>

      <section>
        <SectionHeading title="By model" hint="Sorted by indicative cost" />
        <TableCard title="Efficiency by model" info={EFFICIENCY_METRIC_INFO.byModel} empty={summary.byModel.length === 0} emptyText="No model usage in this range.">
          <MetricHead first={['Model', 'Tool']} />
          <tbody>
            {summary.byModel.map((row) => (
              <tr key={`${row.tool}:${row.model}`} className="border-b border-border last:border-0">
                <td className="py-2.5 pr-3 text-text-default">
                  {row.model}
                  {row.pricedAs === null && ' '}
                  {row.pricedAs === null && (
                    <span className="ml-1.5 rounded-input bg-warn-bg px-1.5 py-0.5 text-[11px] text-text-default">Fallback rate</span>
                  )}
                </td>
                <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[row.tool]}</td>
                <MetricCells row={row} currency={currency} formatCurrency={formatCurrency} />
              </tr>
            ))}
          </tbody>
        </TableCard>
      </section>

      <section>
        <SectionHeading title="By team" hint={`Groups with at least ${summary.minTeamSize} active developers`} />
        <TableCard
          title="Efficiency by team"
          info={EFFICIENCY_METRIC_INFO.byTeam}
          empty={summary.byTeam.length === 0}
          emptyText={`No team has ${summary.minTeamSize} or more active developers in this range. Assign teams in Settings → Developers.`}
        >
          <MetricHead first={['Team', 'Tool', 'Developers']} />
          <tbody>
            {summary.byTeam.map((row) => (
              <tr key={`${row.team}:${row.tool}`} className="border-b border-border last:border-0">
                <td className="py-2.5 pr-3 text-text-default">{row.team}</td>
                <td className="py-2.5 pr-3 text-text-muted">{TOOL_LABELS[row.tool]}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums">{row.developers}</td>
                <MetricCells row={row} currency={currency} formatCurrency={formatCurrency} />
              </tr>
            ))}
          </tbody>
        </TableCard>
      </section>

      <PricingBasisNote
        basis={summary.pricing}
        totalCost={summary.byTool.reduce((sum, t) => sum + t.cost, 0)}
        unpricedModels={summary.unpricedModels}
      >
        Output per cost depends on these prices.
      </PricingBasisNote>
    </>
  );
}

function MetricHead({ first }: { first: string[] }) {
  return (
    <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
      <tr>
        {first.map((label, i) => (
          <th key={label} className={`pb-2.5 pr-3 ${i >= 2 ? 'text-right' : ''}`}>{label}</th>
        ))}
        <th className="pb-2.5 pr-3 text-right">Requests</th>
        <th className="pb-2.5 pr-3 text-right">Cost</th>
        {METRICS.map((m) => (
          <th key={m.key} className="pb-2.5 pr-3 text-right last:pr-0">{m.label}</th>
        ))}
      </tr>
    </thead>
  );
}

function MetricCells({ row, currency, formatCurrency }: { row: EfficiencyMetrics; currency: string; formatCurrency: (value: number, currency: string) => string }) {
  return (
    <>
      <td className="py-2.5 pr-3 text-right tabular-nums">{row.requests.toLocaleString()}</td>
      <td className="py-2.5 pr-3 text-right tabular-nums">{formatCurrency(row.cost, currency)}</td>
      {METRICS.map((m) => (
        <td key={m.key} className="py-2.5 pr-3 text-right tabular-nums last:pr-0">{formatMetric(m.key, row[m.key], currency)}</td>
      ))}
    </>
  );
}

function GoalStatus({ goal, value, format }: { goal: number | undefined; value: number | null; format: (value: number) => string }) {
  if (goal === undefined) return null;
  const met = value !== null && value >= goal;
  return (
    <dd className="text-[11px] text-text-muted">
      <i aria-hidden="true" className={`fa-solid mr-1 ${met ? 'fa-circle-check text-success' : 'fa-circle-exclamation text-warn-ink'}`} />
      Goal ≥ {format(goal)} · {value === null ? 'no data' : met ? 'met' : 'below goal'}
    </dd>
  );
}

function TrendChart({
  metric,
  summary,
  tools,
  goals,
}: {
  metric: (typeof METRICS)[number];
  summary: EfficiencySummary;
  tools: Tool[];
  goals?: Partial<Record<Tool, number>>;
}) {
  const byDate = new Map<string, Record<string, string | number | null>>();
  for (const point of summary.daily) {
    const row = byDate.get(point.date) ?? { date: point.date };
    row[point.tool] = point[metric.key];
    byDate.set(point.date, row);
  }
  const data = [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const isPercent = metric.key === 'reasoningRatio' || metric.key === 'cacheHitRatio';

  return (
    <ChartCard title={metric.label} caption={metric.caption} info={metric.info} height="h-80">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
          <XAxis dataKey="date" {...chartXAxisTickProps} />
          <YAxis
            {...chartAxisTickProps}
            width={64}
            tickFormatter={(value: number) => (isPercent ? `${Math.round(value * 100)}%` : compact.format(value))}
          />
          <Tooltip {...chartTooltipProps} formatter={((value: number | null) => formatMetric(metric.key, value, summary.currency)) as never} />
          <Legend {...chartLegendProps} />
          {tools.map((tool) =>
            goals?.[tool] === undefined ? null : (
              <ReferenceLine
                key={`goal-${tool}`}
                y={goals[tool]}
                ifOverflow="extendDomain"
                stroke={TOOL_COLORS[tool]}
                strokeDasharray="5 4"
                label={{ value: `${TOOL_LABELS[tool]} goal`, position: 'insideTopLeft', fill: 'var(--text-muted)', fontSize: 11 }}
              />
            ),
          )}
          {tools.map((tool) => (
            <Line key={tool} type="monotone" dataKey={tool} name={TOOL_LABELS[tool]} stroke={TOOL_COLORS[tool]} strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
